import React, { useEffect } from 'react';
import { Button } from '../';
import CircuitBreakerStatus from '../CircuitBreakerStatus';
import { useTranslation } from '../../context/TranslationContext';
import { useDecryptedApiKeys } from '../../hooks/useDecryptedApiKeys';

export default function IATab({ 
  models, 
  register, 
  watchedtextAiModel,
  aiWorkingLanguages,
  saveAndTestTextApiKey,
  saveAndTestVisionApiKey,
  saveAndTestTranslationApiKey,
  setValue
}) {
  const { t } = useTranslation();
  
  // Charger les paramètres IA complets seulement pour cet onglet
  const { decryptedKeys, loading, updateKey } = useDecryptedApiKeys(true);

  // Synchroniser les paramètres IA avec le formulaire
  useEffect(() => {
    if (decryptedKeys && setValue) {
      setValue('textAiApiKey', decryptedKeys.textAiApiKey);
      setValue('textAiModel', decryptedKeys.textAiModel);
      setValue('visionAiApiKey', decryptedKeys.visionAiApiKey);
      setValue('visionAiModel', decryptedKeys.visionAiModel);
      setValue('translationAiApiKey', decryptedKeys.translationAiApiKey);
      setValue('translationAiModel', decryptedKeys.translationAiModel);
      
      // Langue de travail depuis routing_config (source unique de vérité)
      const workingLanguage = decryptedKeys.textWorkingLanguage;
      if (workingLanguage) {
        setValue('aiWorkingLanguage', workingLanguage);
      }
    }
  }, [decryptedKeys, setValue]);

  // Synchroniser la sélection de langue quand la liste des langues change
  useEffect(() => {
    if (aiWorkingLanguages.length > 0 && setValue) {
      // Vérifier si la langue actuellement sélectionnée existe dans la nouvelle liste
      const currentValue = decryptedKeys?.textWorkingLanguage;
      if (currentValue && aiWorkingLanguages.some(lang => lang.code === currentValue)) {
        // La langue existe dans la nouvelle liste, l'assigner
        setValue('aiWorkingLanguage', currentValue);
      } else if (aiWorkingLanguages.length > 0) {
        // La langue n'existe pas, sélectionner la première de la liste
        setValue('aiWorkingLanguage', aiWorkingLanguages[0].code);
      }
    }
  }, [aiWorkingLanguages, setValue, decryptedKeys]);

  if (loading) {
    return (
      <div className="wa-form-group">
        <div className="text-center text-gray-500">
          {t('CONFIG', 'LOADING_API_KEYS', 'Chargement des clés API...')}
        </div>
      </div>
    );
  }

  return (
    <div className="wa-form-group">
      {/* Première section: Text IA Model + Working Language */}
      <div className="wa-grid-2">
        <div>
          <label className="wa-label">
            {t('CONFIG', 'CHOOSE_TEXT_IA_MODEL', 'Choose text IA model')} :
          </label>
          <select
            id="textAiModel"
            className="wa-select"
            {...register('textAiModel')}
          >
            <option value="">{t('CONFIG','SELECT_OPTION', 'Select option')}</option>
            {models.text.map(model => (
              <option key={model.id} value={model.model_key}>
                {model.model_name}
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="wa-label">
            {t('CONFIG', 'WORKING_LANGUAGE', 'and working language')} :
          </label>
          <select 
            className="wa-select"
            {...register('aiWorkingLanguage')}
            disabled={!watchedtextAiModel || aiWorkingLanguages.length === 0}
          >
            <option value="">{t('CONFIG', 'SELECT_OPTION', 'Select option')}</option>
            {aiWorkingLanguages.map(lang => (
              <option key={lang.code} value={lang.code}>
                {lang.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Text AI API Key */}
      <div className="wa-form-group wa-form-group-compact">
        <label className="wa-label">
          {t('CONFIG', 'TEXT_AI_API_KEY', 'Text AI API Key')}
        </label>
        <div className="flex items-center">
          <input
            type="text"
            id="textAiApiKey"
            className="wa-input flex-grow"
            {...register('textAiApiKey')}
          />
          <Button
            type="button"
            onClick={saveAndTestTextApiKey}
            variant="primary"
            className="ml-2"
            title={t('CONFIG', 'SAVE_AND_TEST_API_KEY', 'Save and test API Key')}
          >
            <span>✓</span>
          </Button>
        </div>
        <CircuitBreakerStatus 
          route="/api/ia/text" 
          type="large"
          size="md"
        />
      </div>

      {/* Séparateur */}
      <div className="wa-section-divider"></div>

      {/* vision IA Model */}
      <div>
        <label className="wa-label">
          {t('CONFIG', 'CHOOSE_VISION_IA_MODEL', 'Choose vision IA model')} :
        </label>
        <select
          id="visionAiModel"
          className="wa-select"
          {...register('visionAiModel')}
        >
          <option value="">{t('CONFIG', 'SELECT_OPTION', 'Select option')}</option>
          {models.vision.map(model => (
            <option key={model.id} value={model.model_key}>
              {model.model_name}
            </option>
          ))}
        </select>
      </div>

      {/* vision AI API Key */}
      <div className="wa-form-group wa-form-group-compact">
        <label className="wa-label">
          {t('CONFIG', 'VISION_AI_API_KEY', 'Vision AI API Key')}
        </label>
        <div className="flex items-center">
          <input
            type="text"
            id="visionAiApiKey"
            className="wa-input flex-grow"
            {...register('visionAiApiKey')}
          />
          <Button
            variant="primary"
            onClick={saveAndTestVisionApiKey}
            className="ml-2"
            title={t('CONFIG', 'SAVE_AND_TEST_API_KEY', 'Save and test API Key')}
          >
            <span>✓</span>
          </Button>
        </div>
        <CircuitBreakerStatus 
          route="/api/ia/vision" 
          type="large"
          size="md"
        />
      </div>

      {/* Séparateur */}
      <div className="wa-section-divider"></div>

      {/* Translation IA Model */}
      <div>
        <label className="wa-label">
          {t('CONFIG', 'CHOOSE_TRANSLATION_IA_MODEL', 'Choose translation IA model')} :
        </label>
        <select
          id="translationAiModel"
          className="wa-select"
          {...register('translationAiModel')}
        >
        <option value="">{t('CONFIG', 'SELECT_OPTION', 'Select option')}</option>
          {models.translation.map(model => (
            <option key={model.id} value={model.model_key}>
              {model.model_name}
            </option>
          ))}
        </select>
      </div>

      {/* Translation AI API Key / DeepL Key */}
      <div className="wa-form-group wa-form-group-compact">
        <label className="wa-label">
          {t('CONFIG', 'TRANSLATION_AI_API_KEY', 'Translation AI API Key')}
        </label>
        <div className="flex items-center">
          <input
            type="text"
            id="translationAiApiKey"
            className="wa-input flex-grow"
            {...register('translationAiApiKey')}
          />
          <Button
            variant="primary"
            onClick={saveAndTestTranslationApiKey}
            className="ml-2"
            title={t('CONFIG', 'SAVE_AND_TEST_API_KEY', 'Save and test API Key')}
          >
            <span>✓</span>
          </Button>
        </div>
        <CircuitBreakerStatus 
          route="/api/ia/translation" 
          type="large"
          size="md"
        />
      </div>
    </div>
  );
}
